/*============================================================================
  lead-popup.js — Fetch 1.2.1
  ---------------------------------------------------------------------------
  Pop-up de captação de lead com gatilho inteligente.

  Gatilhos (o que ocorrer primeiro, cada um ligável por setting):
    · tempo             — data-trigger-seconds
    · profundidade      — data-trigger-scroll-percent
    · intenção de saída — só desktop (ponteiro fino)

  Nunca dispara:
    · em /cart, /checkout, /password, /challenge (data-page-blocked + guarda de rota)
    · fora das páginas escolhidas em "Show on" (data-page-blocked)
    · na primeira dobra antes de 8s
    · com carrinho, menu drawer ou outro modal abertos
    · quando já visto nesta aba, dentro do prazo, ou já assinado
    · DENTRO DO EDITOR — lá só abre com clique na seção (data-design-mode)

  Frequência (data-frequency):
    session  uma vez por aba
    days     uma vez a cada data-dismiss-days (e uma vez por aba)
    once     uma vez só, nunca mais
    off      nunca abre sozinho; ainda abre pela aba de lembrete

  Persistência:
    localStorage   sn:lead-popup:<sectionId>:seen         timestamp
    localStorage   sn:lead-popup:<sectionId>:subscribed   permanente
    sessionStorage sn:lead-popup:<sectionId>:seen-session trava da aba

  Fila: window.__snPopupQueue garante UM pop-up por sessão. O
  age-verification-popup do tema é adotado na fila com prioridade máxima.

  Vanilla. Sem jQuery. theme.js/theme.a11y são opcionais — nada aqui depende
  deles. As classes aplicadas são as mesmas de theme.Modals, para reaproveitar
  o CSS de modal que o tema já traz.
==============================================================================*/

(function () {
  'use strict';

  var SESSION_KEY = 'sn:popup:session-owner';
  var FIRST_FOLD_FLOOR_MS = 8000;
  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex^="-"])';

  /*========================================================================
    Storage seguro (Safari privado / cookies bloqueados derrubam o acesso)
  ========================================================================*/

  function store(area, key, value) {
    try {
      var s = window[area];
      if (arguments.length === 2) return s.getItem(key);
      if (value === null) {
        s.removeItem(key);
        return null;
      }
      s.setItem(key, value);
      return value;
    } catch (e) {
      return null;
    }
  }

  function readCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  /*========================================================================
    Fila de pop-ups — window.__snPopupQueue

    register(name, {priority})  declara um candidato (menor número = ganha)
    request(name)               pede o slot; true se concedido
    claim(name)                 toma o slot direto (pop-up que abre sozinho)
    resolve(name)               candidato desistiu de abrir nesta página
    release(name)               pop-up fechou (o slot da SESSÃO continua dele)
    owner() / sessionOwner()    quem detém o slot
    reset()                     limpa tudo (editor / debug)
  ========================================================================*/

  function createQueue() {
    var members = [];
    var owner = null;

    function find(name) {
      for (var i = 0; i < members.length; i++) {
        if (members[i].name === name) return members[i];
      }
      return null;
    }

    // Existe candidato mais prioritário que ainda não decidiu se abre?
    function higherPriorityPending(name) {
      var me = find(name);
      var myPriority = me ? me.priority : 50;
      for (var i = 0; i < members.length; i++) {
        if (members[i].name === name) continue;
        if (members[i].priority < myPriority && members[i].state === 'pending') return true;
      }
      return false;
    }

    var api = {
      version: 1,

      register: function (name, options) {
        options = options || {};
        var existing = find(name);
        if (existing) return existing;
        var entry = {
          name: name,
          priority: typeof options.priority === 'number' ? options.priority : 50,
          state: 'pending'
        };
        members.push(entry);
        return entry;
      },

      resolve: function (name) {
        var entry = find(name);
        if (entry) entry.state = 'resolved';
      },

      sessionOwner: function () {
        return store('sessionStorage', SESSION_KEY);
      },

      owner: function () {
        return owner;
      },

      // Outro pop-up já ocupou a sessão (ou está aberto agora)?
      blockedFor: function (name) {
        var session = api.sessionOwner();
        if (session && session !== name) return true;
        if (owner && owner !== name) return true;
        return false;
      },

      claim: function (name) {
        if (api.blockedFor(name)) return false;
        owner = name;
        api.resolve(name);
        store('sessionStorage', SESSION_KEY, name);
        return true;
      },

      request: function (name) {
        if (api.blockedFor(name)) return false;
        if (higherPriorityPending(name)) return false;
        return api.claim(name);
      },

      release: function (name) {
        if (owner === name) owner = null;
      },

      reset: function () {
        owner = null;
        for (var i = 0; i < members.length; i++) members[i].state = 'pending';
        store('sessionStorage', SESSION_KEY, null);
      }
    };

    return api;
  }

  var queue = window.__snPopupQueue || (window.__snPopupQueue = createQueue());

  /*========================================================================
    Adoção do age-verification-popup

    Ele abre sozinho no load (theme.js) e não conhece a fila. Detectamos se
    vai abrir e tomamos o slot da sessão em nome dele.
  ========================================================================*/

  function adoptAgeVerification() {
    var el = document.querySelector('age-verification-popup');
    if (!el) return false;

    queue.register('age-verification', { priority: 0 });

    var alreadyOpen = el.classList.contains('modal--is-active');
    var testMode = el.dataset.testMode === 'true';
    // theme.js grava o cookie usando o próprio id do elemento
    var passed = el.id ? !!readCookie(el.id) : false;
    var willOpen = alreadyOpen || testMode || !passed;

    if (willOpen) {
      queue.claim('age-verification');
      return true;
    }

    queue.resolve('age-verification');
    return false;
  }

  /*========================================================================
    Utilidades
  ========================================================================*/

  // Lê --dur-slow para o cleanup pós-animação ficar amarrado ao token.
  function slowDurationMs() {
    var raw = '';
    try {
      raw = getComputedStyle(document.documentElement).getPropertyValue('--dur-slow').trim();
    } catch (e) {}
    var value = parseFloat(raw);
    if (!value) return 400;
    return raw.indexOf('ms') > -1 ? value : value * 1000;
  }

  function isCommerceRoute() {
    var path = window.location.pathname;
    if (/(^|\/)cart(\/|$)/.test(path)) return true;
    if (/(^|\/)checkouts?(\/|$)/.test(path)) return true;
    if (/(^|\/)(challenge|password)(\/|$)/.test(path)) return true;
    if (window.Shopify && window.Shopify.Checkout) return true;
    return false;
  }

  // Carrinho, menu drawer ou qualquer outro modal ocupando a tela.
  function overlayBusy() {
    var list = document.documentElement.classList;
    return (
      list.contains('cart-open') ||
      list.contains('mobile-nav-open') ||
      list.contains('modal-open') ||
      list.contains('modal-closing') ||
      list.contains('js-drawer-open')
    );
  }

  function inFirstFold() {
    return (window.pageYOffset || document.documentElement.scrollTop || 0) < window.innerHeight * 0.9;
  }

  /*========================================================================
    LeadPopup
  ========================================================================*/

  function LeadPopup(root) {
    this.root = root;
    this.id = root.id;
    this.sectionId = root.dataset.sectionId;
    this.name = 'lead-popup';

    this.inner = root.querySelector('.modal__inner');
    this.form = root.querySelector('form');
    this.isOpen = false;
    this.destroyed = false;
    this.pendingReason = null;
    this.startedAt = Date.now();

    this.testMode = root.dataset.testMode === 'true';
    // Dentro do editor os gatilhos automáticos NÃO armam. O pop-up só abre
    // quando o lojista clica na seção (bindDesignMode). Antes, design mode
    // valia como testMode e o modal reaparecia em toda página do editor.
    this.designMode = root.dataset.designMode === 'true';
    this.useQueue = root.dataset.queue !== 'false';
    this.hasReminder = root.dataset.hasReminder === 'true';

    this.config = {
      time: root.dataset.triggerTime !== 'false',
      seconds: parseInt(root.dataset.triggerSeconds, 10) || 25,
      scroll: root.dataset.triggerScroll !== 'false',
      scrollPercent: parseInt(root.dataset.triggerScrollPercent, 10) || 55,
      exit: root.dataset.triggerExit !== 'false',
      dismissDays: parseInt(root.dataset.dismissDays, 10) || 14,
      // 'session' = uma vez por aba · 'days' = uma vez a cada N dias
      // 'once' = uma vez na vida do visitante · 'off' = nunca sozinho
      frequency: root.dataset.frequency || 'days'
    };

    this.keys = {
      seen: 'sn:lead-popup:' + this.sectionId + ':seen',
      seenSession: 'sn:lead-popup:' + this.sectionId + ':seen-session',
      subscribed: 'sn:lead-popup:' + this.sectionId + ':subscribed',
      legacyCookie: 'newsletter-' + this.sectionId
    };

    this.onKeydown = this.handleKeydown.bind(this);
    this.onFocusin = this.handleFocusin.bind(this);
    this.onRootClick = this.handleRootClick.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.onMouseOut = this.handleMouseOut.bind(this);
    this.onHtmlMutation = this.handleHtmlMutation.bind(this);
    this.onDrawerOpen = this.close.bind(this, 'drawer');

    this.init();
  }

  LeadPopup.prototype.init = function () {
    var self = this;

    this.bindCloseControls();
    this.bindDesignMode();

    // Aberto pela aba de lembrete (snippet newsletter-reminder).
    document.addEventListener('reminder:openNewsletter', function () {
      self.open('reminder', true);
    });

    // Página bloqueada: nada de gatilho, nem de mensagens.
    if (this.root.dataset.pageBlocked === 'true' || isCommerceRoute()) {
      queue.register(this.name, { priority: 10 });
      queue.resolve(this.name);
      return;
    }

    queue.register(this.name, { priority: 10 });

    // Retorno do form: sucesso vira "assinou" permanente; erro reabre.
    if (this.root.querySelector('.note--success')) {
      this.markSubscribed();
      this.open('form-success', true);
      return;
    }

    if (this.root.querySelector('.errors')) {
      this.open('form-error', true);
      return;
    }

    if (this.form) {
      this.form.addEventListener('submit', function () {
        self.markSeen();
      });
    }

    var cta = this.root.querySelector('.newsletter-button');
    if (cta) {
      cta.addEventListener('click', function () {
        self.markSeen();
        self.close('cta');
      });
    }

    if (!this.eligible()) {
      queue.resolve(this.name);
      this.offerReminder();
      return;
    }

    this.armTriggers();
  };

  /*----------------------------------------------------------------------
    Elegibilidade
  ----------------------------------------------------------------------*/

  LeadPopup.prototype.eligible = function () {
    if (this.destroyed) return false;
    if (this.testMode) return true;

    // No editor nada abre sozinho — só o clique na seção, que usa force.
    if (this.designMode) return false;

    if (this.config.frequency === 'off') return false;

    if (store('localStorage', this.keys.subscribed) === '1') return false;

    // Uma vez por aba, sempre — trava independente da regra de dias, para
    // que navegar pelo site nunca traga o pop-up de volta.
    if (store('sessionStorage', this.keys.seenSession) === '1') return false;

    var seen = parseInt(store('localStorage', this.keys.seen), 10);
    if (seen) {
      if (this.config.frequency === 'once') return false;
      var ttl = this.config.dismissDays * 24 * 60 * 60 * 1000;
      if (this.config.frequency === 'days' && Date.now() - seen < ttl) return false;
    }

    // Respeita quem já fechou o pop-up na versão anterior do tema.
    if (readCookie(this.keys.legacyCookie)) return false;

    if (this.useQueue && queue.blockedFor(this.name)) return false;

    return true;
  };

  // Pode abrir NESTE instante? (dobra + overlays concorrentes)
  LeadPopup.prototype.readyNow = function () {
    if (Date.now() - this.startedAt < FIRST_FOLD_FLOOR_MS && inFirstFold()) return false;
    if (overlayBusy()) return false;
    return true;
  };

  /*----------------------------------------------------------------------
    Gatilhos
  ----------------------------------------------------------------------*/

  LeadPopup.prototype.armTriggers = function () {
    var self = this;

    // Editor: sem tempo, sem scroll, sem exit intent. Quem abre é o clique
    // na seção. É o que impede o modal de perseguir o lojista página a página.
    if (this.designMode && !this.testMode) return;

    if (this.config.time) {
      this.timeTimer = window.setTimeout(function () {
        self.request('time');
      }, Math.max(this.config.seconds, 1) * 1000);
    }

    // Piso de 8s: um pedido represado na primeira dobra é reavaliado aqui.
    this.floorTimer = window.setTimeout(function () {
      self.retryPending();
    }, FIRST_FOLD_FLOOR_MS + 100);

    // Página curta demais não tem "profundidade": o gatilho de scroll não arma.
    var doc = document.documentElement;
    this.scrollTriggerArmed = this.config.scroll && doc.scrollHeight > window.innerHeight * 1.4;

    // O listener entra sempre: mesmo sem gatilho de scroll ele é o que destrava
    // um pedido represado pela regra da primeira dobra.
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.config.exit && this.supportsExitIntent()) {
      document.addEventListener('mouseout', this.onMouseOut);
    }

    // Carrinho/menu/modal fecharam? html muda de classe — reavalia.
    if (window.MutationObserver) {
      this.observer = new MutationObserver(this.onHtmlMutation);
      this.observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  };

  LeadPopup.prototype.supportsExitIntent = function () {
    if (!window.matchMedia) return false;
    return matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth >= 769;
  };

  LeadPopup.prototype.handleScroll = function () {
    var self = this;
    if (this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(function () {
      self.scrollFrame = null;

      if (self.pendingReason) self.retryPending();
      if (!self.scrollTriggerArmed) return;

      var doc = document.documentElement;
      var max = Math.max(doc.scrollHeight - window.innerHeight, 1);
      var percent = ((window.pageYOffset || doc.scrollTop || 0) / max) * 100;

      if (percent >= self.config.scrollPercent) self.request('scroll');
    });
  };

  LeadPopup.prototype.handleMouseOut = function (evt) {
    if (evt.relatedTarget || evt.toElement) return;
    if (evt.clientY > 0) return;
    this.request('exit-intent');
  };

  LeadPopup.prototype.handleHtmlMutation = function () {
    if (this.pendingReason) this.retryPending();
  };

  LeadPopup.prototype.retryPending = function () {
    if (!this.pendingReason) return;
    var reason = this.pendingReason;
    this.pendingReason = null;
    this.request(reason);
  };

  // Pedido de abertura: valida, ou represa para reavaliar depois.
  LeadPopup.prototype.request = function (reason) {
    if (this.isOpen || this.destroyed) return;
    if (!this.eligible()) {
      this.teardownTriggers();
      queue.resolve(this.name);
      return;
    }
    if (!this.readyNow()) {
      this.pendingReason = reason;
      return;
    }
    this.open(reason);
  };

  /*----------------------------------------------------------------------
    Abrir / fechar
  ----------------------------------------------------------------------*/

  LeadPopup.prototype.open = function (reason, force) {
    if (this.isOpen || this.destroyed) return;

    if (this.useQueue && !this.testMode && !force) {
      if (!queue.request(this.name)) {
        this.teardownTriggers();
        return;
      }
    } else if (this.useQueue) {
      queue.claim(this.name);
    }

    this.teardownTriggers();
    this.previousFocus = document.activeElement;
    this.openReason = reason;

    this.root.classList.remove('modal--is-closing');
    this.root.classList.add('modal--is-active');
    this.root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.remove('modal-closing');
    document.documentElement.classList.add('modal-open');

    this.isOpen = true;
    // Abrir no editor não queima a regra de frequência do visitante.
    if (!this.testMode && !this.designMode) this.markSeen();

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('focusin', this.onFocusin);
    this.root.addEventListener('click', this.onRootClick);
    document.addEventListener('drawerOpen', this.onDrawerOpen);

    this.focusFirst();

    document.dispatchEvent(new CustomEvent('modalOpen'));
    document.dispatchEvent(new CustomEvent('modalOpen.' + this.id));
    document.dispatchEvent(
      new CustomEvent('leadPopup:open', { detail: { sectionId: this.sectionId, reason: reason } })
    );
  };

  LeadPopup.prototype.close = function (reason) {
    if (!this.isOpen) return;

    var self = this;
    this.isOpen = false;

    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();

    this.root.classList.remove('modal--is-active');
    this.root.classList.add('modal--is-closing');
    this.root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('modal-open');
    document.documentElement.classList.add('modal-closing');

    window.setTimeout(function () {
      self.root.classList.remove('modal--is-closing');
      document.documentElement.classList.remove('modal-closing');
    }, slowDurationMs() + 60);

    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('focusin', this.onFocusin);
    this.root.removeEventListener('click', this.onRootClick);
    document.removeEventListener('drawerOpen', this.onDrawerOpen);

    if (this.previousFocus && this.previousFocus.focus && document.contains(this.previousFocus)) {
      this.previousFocus.focus();
    }
    this.previousFocus = null;

    queue.release(this.name);

    document.dispatchEvent(new CustomEvent('modalClose.' + this.id));
    document.dispatchEvent(
      new CustomEvent('leadPopup:close', { detail: { sectionId: this.sectionId, reason: reason } })
    );
  };

  /*----------------------------------------------------------------------
    Fechamento: Esc, backdrop, botão. Foco preso enquanto aberto.
  ----------------------------------------------------------------------*/

  LeadPopup.prototype.bindCloseControls = function () {
    var self = this;
    this.root.querySelectorAll('.js-modal-close').forEach(function (btn) {
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        evt.stopPropagation();
        self.close('button');
      });
    });
  };

  LeadPopup.prototype.handleRootClick = function (evt) {
    if (evt.target.closest('.js-modal-close')) return;
    if (evt.target.closest('.modal__inner')) return;
    this.close('backdrop');
  };

  LeadPopup.prototype.focusableElements = function () {
    var scope = this.inner || this.root;
    return Array.prototype.filter.call(scope.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  };

  LeadPopup.prototype.focusFirst = function () {
    var self = this;
    // Espera a animação começar para o leitor de tela anunciar o diálogo.
    window.setTimeout(function () {
      if (!self.isOpen) return;
      var items = self.focusableElements();
      var target = self.root.querySelector('input[type="email"]') || items[0] || self.root;
      if (target && target.focus) target.focus();
    }, 80);
  };

  LeadPopup.prototype.handleKeydown = function (evt) {
    if (!this.isOpen) return;

    if (evt.key === 'Escape' || evt.keyCode === 27) {
      evt.preventDefault();
      this.close('esc');
      return;
    }

    if (evt.key !== 'Tab' && evt.keyCode !== 9) return;

    var items = this.focusableElements();
    if (!items.length) {
      evt.preventDefault();
      this.root.focus();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (evt.shiftKey && (active === first || !this.root.contains(active))) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && active === last) {
      evt.preventDefault();
      first.focus();
    }
  };

  LeadPopup.prototype.handleFocusin = function (evt) {
    if (!this.isOpen) return;
    if (this.root.contains(evt.target)) return;
    var items = this.focusableElements();
    (items[0] || this.root).focus();
  };

  /*----------------------------------------------------------------------
    Persistência
  ----------------------------------------------------------------------*/

  LeadPopup.prototype.markSeen = function () {
    store('localStorage', this.keys.seen, String(Date.now()));
    store('sessionStorage', this.keys.seenSession, '1');
  };

  LeadPopup.prototype.markSubscribed = function () {
    store('localStorage', this.keys.subscribed, '1');
    this.markSeen();
  };

  // Já viu o pop-up e existe aba de lembrete: mostra a aba no lugar.
  // Se outro pop-up (age gate) domina a sessão, nem a aba aparece.
  LeadPopup.prototype.offerReminder = function () {
    if (!this.hasReminder || this.testMode) return;
    if (store('localStorage', this.keys.subscribed) === '1') return;
    if (this.useQueue && queue.blockedFor(this.name)) return;
    document.dispatchEvent(new CustomEvent('newsletter:openReminder', { bubbles: true }));
  };

  /*----------------------------------------------------------------------
    Ciclo de vida
  ----------------------------------------------------------------------*/

  LeadPopup.prototype.teardownTriggers = function () {
    window.clearTimeout(this.timeTimer);
    window.clearTimeout(this.floorTimer);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('mouseout', this.onMouseOut);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.pendingReason = null;
  };

  LeadPopup.prototype.destroy = function () {
    this.close('unload');
    this.teardownTriggers();
    this.destroyed = true;
  };

  LeadPopup.prototype.bindDesignMode = function () {
    if (!window.Shopify || !window.Shopify.designMode) return;
    var self = this;

    document.addEventListener('shopify:section:select', function (evt) {
      if (evt.detail.sectionId !== self.sectionId) return;
      queue.reset();
      self.open('editor', true);
    });

    document.addEventListener('shopify:section:deselect', function (evt) {
      if (evt.detail.sectionId !== self.sectionId) return;
      self.close('editor');
    });

    document.addEventListener('shopify:block:select', function (evt) {
      if (evt.detail.sectionId !== self.sectionId) return;
      self.close('editor');
    });

    document.addEventListener('shopify:block:deselect', function (evt) {
      if (evt.detail.sectionId !== self.sectionId) return;
      self.open('editor', true);
    });

    document.addEventListener('shopify:section:unload', function (evt) {
      if (evt.detail.sectionId !== self.sectionId) return;
      self.destroy();
    });
  };

  /*========================================================================
    Boot
  ========================================================================*/

  function boot() {
    adoptAgeVerification();

    document.querySelectorAll('[data-section-type="lead-popup"]').forEach(function (root) {
      if (root.dataset.leadPopupReady === 'true') return;
      root.dataset.leadPopupReady = 'true';
      root.__leadPopup = new LeadPopup(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // O editor re-injeta a seção via innerHTML, então o <script> não roda de novo.
  document.addEventListener('shopify:section:load', function () {
    boot();
  });
})();
